import './style.scss'
import { isAllRoomsHaveAtLeastOneImage, isFactsheetHavingFactsAssignedToRooms } from '../helpers'
import { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { preferencesActions } from '../../../../redux/actions/preferences'
import { useTranslation } from 'react-i18next'
import activityTrackingService from '../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../constants'
import { usePrevious } from '../../../../customHooks/usePrevious'

const propertyChecklistItems = [
  {
    // saved factsheet
    label: 'PROPERTY_CHECKLIST_ITEM_HEAD_SAVED_PROPERTY_FACTS',
    descriptionLabel: 'PROPERTY_CHECKLIST_ITEM_DESC_SAVED_PROPERTY_FACTS',
    func: property => property?.status?.propertyInfo === 3
      && Object.keys(property?.factsheet?.facts ?? {}).length > 1,
    requiresEditProperty: true
  },
  {
    // created room types
    label: 'PROPERTY_CHECKLIST_ITEM_HEAD_ADD_ROOM_TYPES',
    descriptionLabel: 'PROPERTY_CHECKLIST_ITEM_DESC_ADD_ROOM_TYPES',
    func: (_, ___, rooms) => rooms.length > 0,
  },
  {
    // 15 images uploaded
    label: 'PROPERTY_CHECKLIST_ITEM_HEAD_UPLOAD_AT_LEAST_15_IMAGES',
    descriptionLabel: 'PROPERTY_CHECKLIST_ITEM_DESC_UPLOAD_AT_LEAST_15_IMAGES',
    func: (_, images) => images.length > 14,
  },
  {
    // assigned facts to rooms
    label: 'PROPERTY_CHECKLIST_ITEM_HEAD_ASSIGN_FACTS_ROOMS',
    descriptionLabel: 'PROPERTY_CHECKLIST_ITEM_DESC_ASSIGN_FACTS_ROOMS',
    func: (property) => isFactsheetHavingFactsAssignedToRooms(property?.factsheet?.facts),
    requiresEditProperty: true
  },
  {
    // all rooms have images
    label: 'PROPERTY_CHECKLIST_ITEM_HEAD_ADD_AT_LEAST_ONE_IMAGE_PER_ROOM',
    descriptionLabel: 'PROPERTY_CHECKLIST_ITEM_DESC_ADD_AT_LEAST_ONE_IMAGE_PER_ROOM',
    func: (_, images, rooms) => isAllRoomsHaveAtLeastOneImage(images, rooms),
  }
]

const PropertyChecklistProgress = ({ progress }) => {
  return <div className="property-checklist-progress-container">
    <div className="property-checklist-progress-text">{progress}%</div>
    <div className="property-checklist-progress-bar">
      <div className="property-checklist-progress-bar-indicator" style={{ width: progress + '%' }}></div>
    </div>
  </div>
}

const PropertyChecklist = memo(({
                                  ready,
                                  property,
                                  images,
                                  rooms,
                                  includeEditPropertyItems = false,
                                }) => {
  const { t } = useTranslation()
  const propertyChecklistDismissed = useSelector(state => state.preferences.propertyChecklistDismissed)
  const dispatch = useDispatch()
  const [expanded, setExpanded] = useState(false)
  const [progress, setProgress] = useState(0)
  const [alreadyCompleted, setAlreadyCompleted] = useState(false)
  const prevProgress = usePrevious(progress)
  const [checklistItems, setChecklistItems] = useState([])

  useEffect(() => {
    if (includeEditPropertyItems) {
      setChecklistItems(propertyChecklistItems)
      return
    }
    setChecklistItems(propertyChecklistItems.filter(item => item.requiresEditProperty !== true))
  }, [includeEditPropertyItems])

  const calculateProgress = useCallback(() => {
    const progress = checklistItems.reduce((progress, item) => {
      if (item.func(property, images, rooms)) {
        return progress + 1
      }
      return progress
    }, 0)
    return Math.floor((progress / checklistItems.length) * 100)
  }, [property, images, rooms, checklistItems])

  useEffect(() => {
    if (!ready) {
      return
    }
    setProgress(calculateProgress)
  }, [property, images, rooms, calculateProgress, ready])

  const toggle = useCallback(() => {
    if (expanded) {
      if (progress === 100) {
        setAlreadyCompleted(true)
      }
      activityTrackingService.trackEvent(trackingEvent.closePropertyChecklist)
    } else {
      activityTrackingService.trackEvent(trackingEvent.openPropertyChecklist)
    }
    setExpanded(!expanded)
  }, [expanded, setExpanded, progress])

  useEffect(() => {
    if (prevProgress === 0 && progress === 100) {
      setAlreadyCompleted(true)
      return
    }
    if (prevProgress > 0 && progress > prevProgress && !expanded) {
      if (progress === 100) {
        // TODO: Improve accuracy
        activityTrackingService.trackEvent(trackingEvent.completePropertyChecklist)
      }
      toggle()
    }
  }, [progress, prevProgress, expanded, toggle])

  if (propertyChecklistDismissed || !ready || alreadyCompleted) {
    return null
  }

  let className = 'property-checklist-container'
  if (expanded) {
    className += ' expanded'
  }
  if (progress === 100) {
    className += ' completed'
  }
  const completed = progress === 100

  return (
    <div
      className={className}
      onClick={e => {
        e.stopPropagation()
        toggle()
      }}
    >
      <div className="property-checklist" onClick={e => {
        e.stopPropagation()
        if (!expanded) {
          toggle()
        }
      }}>
        <div className="property-checklist-header">
          {
            expanded && completed
              ? t('PROPERTY_CHECKLIST_COMPLETED_HEADER')
              : (expanded ? t('PROPERTY_CHECKLIST_HEADER') : t('PROPERTY_CHECKLIST_HEADER_COLLAPSED'))
          }
        </div>
        {expanded && completed && <>
          <i className="property-checklist-completed-close-icon" onClick={toggle}></i>
          <i className="property-checklist-completed-icon"></i>
          <div className="property-checklist-completed-message">
            {t('PROPERTY_CHECKLIST_COMPLETED_MESSAGE')}
          </div>
        </>
        }
        {expanded && !completed && <>
          <span className="property-checklist-subheader">{t('PROPERTY_CHECKLIST_SUBHEADER')}</span>
          <br/>
        </>
        }
        {(!expanded || !completed) && <PropertyChecklistProgress progress={progress}/>}
        {!completed && <div className="property-checklist-items-container">
          {
            checklistItems.map(item => {
              const checked = item.func(property, images, rooms)
              const className = checked ? 'checklist-item checked' : 'checklist-item'
              return (
                <div className={className} key={item.label}>
                  <b>{t(item.label)}</b>
                  <br/>
                  {t(item.descriptionLabel)}
                </div>
              )
            })
          }
        </div>
        }
        <div
          onClick={(e) => {
            e.stopPropagation()
            dispatch(preferencesActions.dismissPropertyChecklist())
            activityTrackingService.trackEvent(trackingEvent.dismissPropertyChecklist)
          }}
          className="property-checklist-dismiss-cta"
        >
          {t('PROPERTY_CHECKLIST_DISMISS')}
        </div>
      </div>
      <button onClick={toggle} className="property-checklist-container-close-btn" type="button"></button>
    </div>
  )
})

export default PropertyChecklist
